import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  BarChart3, Users, ClipboardList, MessagesSquare, Activity, ShieldCheck,
  Download, RefreshCw, Search, Rocket, Check, X, Eye,
  Sparkles, Inbox, Clock, PauseCircle, CheckCircle2, AlertTriangle,
  ExternalLink, MessageCircle, UserCheck, ScrollText,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { toast } from "sonner";

import { SiteShell } from "@/components/layout/SiteShell";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { AdminConversationsPanel } from "@/components/admin/AdminConversationsPanel";
import { ProfileNameLink } from "@/components/admin/ProfileNameLink";
import EarlyAccessCommunityAgreementModal from "@/components/EarlyAccessCommunityAgreementModal";
import EarlyAccessCommsNoticeModal from "@/components/EarlyAccessCommsNoticeModal";

// Palette pulled from CSS tokens so the charts stay on-brand.
const C = {
  forest: "hsl(150 32% 52%)",
  moss: "hsl(155 22% 24%)",
  amber: "hsl(36 80% 62%)",
  amber2: "hsl(40 70% 50%)",
  clay: "hsl(8 55% 62%)",
  cream: "hsl(36 40% 78%)",
  ink: "hsl(42 50% 94%)",
  inkDim: "hsl(42 24% 82%)",
  grid: "hsl(150 20% 30% / 0.35)",
};

const TABS = [
  { id: "insights", label: "Insights", icon: Sparkles },
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "users", label: "Users", icon: Users },
  { id: "projects", label: "Projects", icon: ClipboardList },
  { id: "conversations", label: "Conversations", icon: MessageCircle },
  { id: "engagement", label: "Engagement", icon: MessagesSquare },
  { id: "traffic", label: "Traffic", icon: Activity },
  { id: "terms", label: "Terms & Privacy", icon: ShieldCheck },
  { id: "early_access", label: "Early Access", icon: UserCheck },
];

// Priority bucket configuration — order & label match the owner's
// "what needs my attention" spec. The `rank` mirrors the backend so
// the tab reads left-to-right in priority order.
const PRIORITY_BUCKETS = [
  {
    id: "new_applications",
    rank: 1,
    label: "New applications",
    description: "Real projects with fresh applications waiting for you to review.",
    tone: "border-[hsl(var(--sb-forest))]/60 bg-[hsl(var(--sb-forest))]/12 text-[hsl(var(--sb-forest-2))]",
    icon: Inbox,
  },
  {
    id: "waiting_author",
    rank: 2,
    label: "Waiting on author action",
    description: "Applications that have been sitting > 3 days without an accept or decline.",
    tone: "border-[hsl(var(--sb-clay))]/60 bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay-2))]",
    icon: AlertTriangle,
  },
  {
    id: "accepted_idle",
    rank: 3,
    label: "Accepted projects with no activity",
    description: "Reader accepted but the project has been idle for more than 10 days.",
    tone: "border-[hsl(var(--sb-gold))]/60 bg-[hsl(var(--sb-gold))]/12 text-[hsl(var(--sb-gold))]",
    icon: Clock,
  },
  {
    id: "stalled",
    rank: 4,
    label: "Stalled real projects",
    description: "Open for more than 7 days without a single application.",
    tone: "border-border bg-muted/40 text-foreground/80",
    icon: PauseCircle,
  },
  {
    id: "recently_completed",
    rank: 5,
    label: "Recently completed",
    description: "Finished within the last 30 days — worth checking for follow-ups or testimonials.",
    tone: "border-[hsl(var(--sb-emerald))]/50 bg-[hsl(150_28%_20%)]/30 text-[hsl(40_60%_96%)]",
    icon: CheckCircle2,
  },
];

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-border bg-[hsl(var(--sb-surface))] p-5 ${className}`}>
      {children}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <Card>
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-2 font-serif text-3xl">{value ?? "—"}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function DownloadBtn({ metric, days = 30, label }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  return (
    <a
      href={`${API}/api/admin/analytics/export?metric=${metric}&days=${days}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border border-border hover:bg-muted"
      data-testid={`analytics-export-${metric}`}
    >
      <Download size={14} /> {label || "CSV"}
    </a>
  );
}

function ChartFrame({ title, right, children }) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-serif text-lg">{title}</h3>
        {right}
      </div>
      <div className="h-64">{children}</div>
    </Card>
  );
}

// ─── Launch Mode toggle card ────────────────────────────────────────────
function LaunchModeCard({ settings, onChange, previewHref }) {
  const on = !!settings?.launch_mode;
  return (
    <Card className="border-[hsl(var(--sb-gold))]/40">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--sb-gold-2))]">
            <Rocket size={14} /> Launch Mode
          </div>
          <div className="mt-2 font-serif text-2xl">
            Terms &amp; Privacy gate is <span className={on ? "text-[hsl(var(--sb-clay-2))]" : "text-[hsl(var(--sb-forest-2))]"}>{on ? "ENFORCED" : "OFF"}</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            When ON, every user must accept the current Terms &amp; Privacy Policy
            (v{settings?.current_terms_version}) before entering the platform.
            Keep this OFF during beta so existing testers aren&apos;t locked out.
          </p>
          <a href={previewHref} className="mt-3 inline-flex items-center gap-1.5 text-sm underline text-[hsl(var(--sb-gold-2))]" data-testid="analytics-preview-gate">
            <Eye size={14} /> Preview the gate for yourself (does not affect other users)
          </a>
        </div>
        <button
          type="button"
          onClick={() => onChange(!on)}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${on ? "bg-[hsl(var(--sb-clay))]" : "bg-muted"}`}
          data-testid="analytics-launch-toggle"
          aria-pressed={on}
        >
          <span className={`inline-block h-6 w-6 transform rounded-full bg-[hsl(var(--sb-cream))] shadow transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
        </button>
      </div>
    </Card>
  );
}

// ─── Project management table + detail modal ───────────────────────────
function StatusBadge({ status }) {
  const map = {
    open: "bg-[hsl(var(--sb-forest))]/20 text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest))]/40",
    in_progress: "bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))] border-[hsl(var(--sb-gold))]/50",
    completed: "bg-[hsl(150_28%_20%)] text-[hsl(40_60%_96%)] border-[hsl(var(--sb-emerald))]",
    cancelled: "bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))] border-[hsl(var(--sb-clay))]/50",
    closed: "bg-muted text-muted-foreground border-border",
  };
  const cls = map[status] || map.closed;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${cls}`}>{status || "—"}</span>;
}

function ProjectManagementTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const [listing, setListing] = useState("all");
  const [visibility, setVisibility] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, listing, visibility, search });
      const { data } = await api.get(`/admin/analytics/projects/list?${params.toString()}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, listing, visibility, search]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (id, action, msg) => {
    if (action === "close" && !window.confirm(
      "Close this project?\n\nThis will mark the project as closed and stop new applications. Existing applications, accepted readers, Coffee Chats, and messages are preserved. You can reopen it later from this menu.\n\nNote: this is different from the Close button that only shuts the admin detail window."
    )) return;
    if (action === "cancel" && !window.confirm("Cancel this project? Existing accepted readers keep manuscript access, but the project will be marked cancelled.")) return;
    if (action === "reopen" && !window.confirm(
      "Reopen this project? The project will be restored to its previous active status and applications will be re-enabled. All existing data is preserved."
    )) return;
    try {
      await api.post(`/admin/analytics/projects/${id}/action`, { action });
      toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-serif text-lg">Project management</h3>
          <p className="text-xs text-muted-foreground mt-1">Inspect and manage every project. {rows.length} shown.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="Title, author, email, or project ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-full border border-border bg-background pl-8 pr-3 py-1.5 text-sm w-64"
              data-testid="analytics-projects-search"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-border bg-background px-3 py-1.5 text-sm" data-testid="analytics-projects-status">
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="closed">Closed</option>
          </select>
          <select value={listing} onChange={(e) => setListing(e.target.value)} className="rounded-full border border-border bg-background px-3 py-1.5 text-sm" data-testid="analytics-projects-listing">
            <option value="all">Demo + Real</option>
            <option value="real">Real projects</option>
            <option value="demo">Demo / Test</option>
          </select>
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="rounded-full border border-border bg-background px-3 py-1.5 text-sm" data-testid="analytics-projects-visibility">
            <option value="all">All visibilities</option>
            <option value="default">Private Draft only</option>
            <option value="public">Public only</option>
            <option value="private_testers">Private Testers Only</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-2 px-2">Title</th>
              <th className="py-2 px-2">Author</th>
              <th className="py-2 px-2">Type</th>
              <th className="py-2 px-2">Visibility</th>
              <th className="py-2 px-2">Status</th>
              <th className="py-2 px-2">Accepted</th>
              <th className="py-2 px-2">Apps</th>
              <th className="py-2 px-2">Created</th>
              <th className="py-2 px-2">Last activity</th>
              <th className="py-2 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-muted-foreground">No projects match.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-muted/40">
                <td className="py-2 px-2 max-w-[240px]">
                  <div className="font-medium truncate" title={r.title}>{r.title || "—"}</div>
                  <div className="text-[10px] text-muted-foreground font-mono truncate">{r.id}</div>
                </td>
                <td className="py-2 px-2 max-w-[180px]">
                  <div className="truncate" title={r.author_name}>{r.author_name || "—"}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{r.author_email || ""}</div>
                </td>
                <td className="py-2 px-2">
                  {r.listing_type === "demo" || r.is_demo ? (
                    <span className="inline-flex rounded-full border border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay))] text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5">Demo</span>
                  ) : (
                    <span className="inline-flex rounded-full border border-border bg-muted/40 text-foreground/80 text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5">Real</span>
                  )}
                </td>
                <td className="py-2 px-2">
                  {r.visibility === "default" && <span className="inline-flex rounded-full border border-[hsl(var(--sb-gold))]/50 bg-[hsl(var(--sb-gold))]/12 text-[hsl(var(--sb-gold))] text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5">Private Draft</span>}
                  {r.visibility === "public" && <span className="inline-flex rounded-full border border-[hsl(var(--sb-forest))]/50 bg-[hsl(var(--sb-forest))]/12 text-[hsl(var(--sb-forest-2))] text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5">Public</span>}
                  {r.visibility === "private_testers" && <span className="inline-flex rounded-full border border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay))] text-[10px] font-bold uppercase tracking-[0.1em] px-2 py-0.5">Private Testers</span>}
                </td>
                <td className="py-2 px-2">
                  <StatusBadge status={r.status} />
                  {r.applications_closed && <div className="mt-1 text-[10px] text-[hsl(var(--sb-clay))]">Apps paused</div>}
                </td>
                <td className="py-2 px-2 max-w-[160px]">
                  <div className="font-medium">{r.accepted_readers_count || 0}</div>
                  {r.accepted_reader_name && <div className="text-[10px] text-muted-foreground truncate" title={r.accepted_reader_name}>{r.accepted_reader_name}</div>}
                </td>
                <td className="py-2 px-2">{r.applications_count || 0}</td>
                <td className="py-2 px-2 text-[11px] text-muted-foreground whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                <td className="py-2 px-2 text-[11px] text-muted-foreground whitespace-nowrap">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</td>
                <td className="py-2 px-2 text-right whitespace-nowrap">
                  <button onClick={() => setSelected(r.id)} className="text-xs px-2 py-1 rounded-full border border-border hover:bg-muted mr-1" data-testid={`analytics-projects-view-${r.id}`}>View</button>
                  <div className="relative inline-block">
                    <details>
                      <summary className="list-none cursor-pointer text-xs px-2 py-1 rounded-full border border-border hover:bg-muted">Actions ▾</summary>
                      <div className="absolute right-0 z-20 mt-1 rounded-lg border border-border bg-popover shadow-lg overflow-hidden min-w-[140px]">
                        {!r.applications_closed && r.status === "open" && <button onClick={() => doAction(r.id, "pause", "Applications paused.")} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted">Pause applications</button>}
                        {r.applications_closed && <button onClick={() => doAction(r.id, "resume", "Applications resumed.")} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted">Resume applications</button>}
                        {r.status !== "closed" && <button onClick={() => doAction(r.id, "close", "Project closed.")} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted">Close Project</button>}
                        {r.status !== "cancelled" && <button onClick={() => doAction(r.id, "cancel", "Project cancelled.")} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted text-[hsl(var(--sb-clay))]">Cancel project</button>}
                        {(r.status === "closed" || r.status === "cancelled") && <button onClick={() => doAction(r.id, "reopen", "Project reopened.")} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted text-[hsl(var(--sb-forest-2))]" data-testid={`admin-project-reopen-${r.id}`}>Reopen Project</button>}
                      </div>
                    </details>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <ProjectDetailModal id={selected} onClose={() => setSelected(null)} onAction={doAction} />}
    </Card>
  );
}

function ProjectDetailModal({ id, onClose, onAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Thread id currently open in the nested moderation viewer (see
  // AdminThreadViewerModal). Kept local to this modal so opening a
  // conversation doesn't affect any of the outer analytics state.
  const [viewingThread, setViewingThread] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/analytics/projects/${id}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-background p-6 shadow-2xl" data-testid="analytics-project-detail-modal">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--sb-gold))]">Project detail</div>
            <h3 className="mt-1 font-serif text-2xl">{data?.job?.title || "Loading…"}</h3>
            {data?.job && (
              <div className="mt-1 text-xs text-muted-foreground font-mono">{data.job.id}</div>
            )}
          </div>
          <button onClick={onClose} className="text-sm rounded-full border border-border px-3 py-1 hover:bg-muted" data-testid="admin-project-detail-close-window">Close window</button>
        </div>

        {loading && <div className="py-8 text-center text-muted-foreground">Loading…</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div><span className="text-muted-foreground">Author:</span> {data.job.author_name || "—"}</div>
              <div><span className="text-muted-foreground">Status:</span> <StatusBadge status={data.job.status} /></div>
              <div><span className="text-muted-foreground">Type:</span> {data.job.listing_type === "demo" || data.job.is_demo ? "Demo" : "Real"}</div>
              <div><span className="text-muted-foreground">Accepted:</span> {data.job.accepted_readers_count}</div>
              <div><span className="text-muted-foreground">Apps:</span> {data.job.applications_count}</div>
              <div><span className="text-muted-foreground">Apps closed:</span> {data.job.applications_closed ? "yes" : "no"}</div>
              <div><span className="text-muted-foreground">Created:</span> {data.job.created_at ? new Date(data.job.created_at).toLocaleString() : "—"}</div>
              <div><span className="text-muted-foreground">Deadline:</span> {data.job.deadline_at ? new Date(data.job.deadline_at).toLocaleDateString() : "—"}</div>
              <div><span className="text-muted-foreground">Last activity:</span> {data.job.updated_at ? new Date(data.job.updated_at).toLocaleString() : "—"}</div>
            </div>

            {/* Phase B.2: project-level confidentiality summary. */}
            <ConfidentialitySummary summary={data.confidentiality_summary} />


            <div className="mt-5 flex flex-wrap gap-2">
              <a href={`/jobs/${id}`} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted">Open project page ↗</a>
              {!data.job.applications_closed && <button onClick={() => onAction(id, "pause", "Applications paused.")} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted">Pause applications</button>}
              {data.job.applications_closed && <button onClick={() => onAction(id, "resume", "Applications resumed.")} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted">Resume applications</button>}
              {data.job.status !== "closed" && data.job.status !== "cancelled" && (
                <button
                  onClick={() => onAction(id, "close", "Project closed.")}
                  className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted"
                  data-testid="admin-project-close-action"
                >
                  Close Project
                </button>
              )}
              {(data.job.status === "closed" || data.job.status === "cancelled") && (
                <button
                  onClick={() => onAction(id, "reopen", "Project reopened.")}
                  className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--sb-forest))]/60 text-[hsl(var(--sb-forest-2))] hover:bg-[hsl(var(--sb-forest))]/10"
                  data-testid="admin-project-reopen-action"
                >
                  Reopen Project
                </button>
              )}
              {data.job.status !== "cancelled" && <button onClick={() => onAction(id, "cancel", "Project cancelled.")} className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--sb-clay))]/50 text-[hsl(var(--sb-clay))] hover:bg-[hsl(var(--sb-clay))]/10">Cancel</button>}
            </div>

            <Section title={`Applications (${data.applications.length})`}>
              {data.applications.length === 0 && <div className="text-xs text-muted-foreground">No applications.</div>}
              {data.applications.map((a) => (
                <div key={a.id} className="rounded-lg border border-border p-3 text-xs" data-testid={`analytics-application-${a.id}`}>
                  <div className="flex justify-between gap-2">
                    <div className="font-medium">{a.reader_email || a.reader_id}</div>
                    <StatusBadge status={a.status} />
                  </div>
                  {a.cover_letter && <div className="mt-1 text-muted-foreground line-clamp-2">{a.cover_letter}</div>}
                  <div className="mt-1 text-[10px] text-muted-foreground">Applied {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}</div>
                  {a.confidentiality && (
                    <ApplicationConfidentialityChips conf={a.confidentiality} appId={a.id} />
                  )}
                </div>
              ))}
            </Section>

            <Section title={`Coffee chats (${data.coffee_chats.length})`}>
              {data.coffee_chats.length === 0 && <div className="text-xs text-muted-foreground">No coffee chats tied to this project.</div>}
              {data.coffee_chats.map((c) => {
                const requesterIsAuthor = c.requester?.id && c.author?.id && c.requester.id === c.author.id;
                return (
                  <div key={c.id} className="rounded-lg border border-border p-3 text-xs space-y-1.5" data-testid={`analytics-coffee-chat-${c.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mr-1">Initiated by</span>
                        <span className="font-semibold">{requesterIsAuthor ? "Author" : "Reader"}</span>
                        <span className="text-muted-foreground">
                          {" — "}
                          {c.requester?.display_name || c.requester?.email || c.requester?.id || "—"}
                        </span>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                    <ParticipantLine label="Author" u={c.author} />
                    <ParticipantLine label="Reader" u={c.reader} />
                    {c.message && (
                      <div className="mt-1 rounded bg-muted/50 border border-border/60 p-2 whitespace-pre-wrap break-words text-[11px]">
                        {c.message}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
                      <div className="text-[10px] text-muted-foreground">
                        Initiated {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                        {c.updated_at && c.updated_at !== c.created_at && (
                          <>{" · Updated "}{new Date(c.updated_at).toLocaleString()}</>
                        )}
                      </div>
                      {c.thread_id ? (
                        <button
                          onClick={() => setViewingThread(c.thread_id)}
                          className="text-[11px] px-2 py-1 rounded-full border border-[hsl(var(--sb-gold))]/60 hover:bg-[hsl(var(--sb-gold))]/10"
                          data-testid={`analytics-coffee-chat-open-thread-${c.id}`}
                        >
                          Open conversation →
                        </button>
                      ) : (
                        <span className="text-[10px] text-muted-foreground italic">
                          {c.status === "proposed" ? "Pending — no thread yet." :
                           c.status === "declined" ? "Declined — no thread." :
                           c.status === "cancelled" ? "Cancelled — no thread." :
                           "No linked thread."}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </Section>

            {data.threads && (
              <Section title={`Threads (${data.threads.length})`}>
                {data.threads.length === 0 && (
                  <div className="text-xs text-muted-foreground">No message threads tied to this project.</div>
                )}
                {data.threads.map((t) => {
                  const preview = t.latest_message;
                  const senderLabel = preview?.sender?.display_name || preview?.sender?.email || preview?.sender?.id || "—";
                  const bodySnippet = (preview?.body || "").length > 200
                    ? `${preview.body.slice(0, 200)}…`
                    : (preview?.body || "");
                  return (
                    <div key={t.id} className="rounded-lg border border-border p-3 text-xs space-y-1.5" data-testid={`analytics-thread-${t.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-semibold text-[11px]">
                            {(t.participants || []).map((p) => p?.display_name || p?.email || p?.id).filter(Boolean).join(" · ") || "—"}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-mono truncate" title={t.id}>{t.id}</div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          {!t.belongs_to_project && (
                            <span
                              className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border border-[hsl(var(--sb-clay))]/60 text-[hsl(var(--sb-clay))] bg-[hsl(var(--sb-clay))]/10"
                              title="This thread's job_id doesn't match this project — surfaced because both participants are also connected to this project via an application or coffee chat."
                              data-testid={`analytics-thread-cross-${t.id}`}
                            >
                              cross-project
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-border">{t.status}</span>
                        </div>
                      </div>
                      {preview ? (
                        <div className="mt-1 rounded bg-muted/50 border border-border/60 p-2 text-[11px]" data-testid={`analytics-thread-preview-${t.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="text-[11px] font-medium min-w-0 truncate">
                              {senderLabel}
                              {preview.sender?.is_demo && <span className="ml-1 text-[9px] px-1 rounded bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))]">demo</span>}
                              {preview.sender?.is_private_test && <span className="ml-1 text-[9px] px-1 rounded bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold-2))]">pta</span>}
                            </div>
                            <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                              {preview.created_at ? new Date(preview.created_at).toLocaleString() : ""}
                            </div>
                          </div>
                          <div className="mt-1 whitespace-pre-wrap break-words text-[11px]">{bodySnippet}</div>
                        </div>
                      ) : (
                        <div className="text-[10px] text-muted-foreground italic">No messages yet.</div>
                      )}
                      <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
                        <div className="text-[10px] text-muted-foreground">
                          {t.message_count > 0 ? `${t.message_count} message${t.message_count === 1 ? "" : "s"}` : "0 messages"}
                          {t.last_message_at && (
                            <>{" · Last "}{new Date(t.last_message_at).toLocaleString()}</>
                          )}
                        </div>
                        <button
                          onClick={() => setViewingThread(t.id)}
                          className="text-[11px] px-2 py-1 rounded-full border border-[hsl(var(--sb-gold))]/60 hover:bg-[hsl(var(--sb-gold))]/10"
                          data-testid={`analytics-thread-open-${t.id}`}
                        >
                          Open conversation →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </Section>
            )}

            <Section title={`Status & acceptance history (${(data.history || []).length})`}>
              {(data.history || []).length === 0 && <div className="text-xs text-muted-foreground">No events recorded yet. New status changes and application accept/decline actions will appear here.</div>}
              {(data.history || []).map((h) => {
                const tone = h.event?.startsWith("status.")
                  ? "border-[hsl(var(--sb-gold))]/50 bg-[hsl(var(--sb-gold))]/8"
                  : h.event?.startsWith("application.accepted")
                    ? "border-[hsl(var(--sb-forest))]/50 bg-[hsl(var(--sb-forest))]/8"
                    : h.event?.startsWith("application.declined") || h.event === "admin.cancel"
                      ? "border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/8"
                      : "border-border";
                return (
                  <div key={h.id} className={`rounded-lg border p-2 text-xs ${tone}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold uppercase tracking-[0.1em] text-[11px]">{h.event}</div>
                      <div className="text-[10px] text-muted-foreground whitespace-nowrap">{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</div>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">by {h.actor_email || h.actor_id || "system"}{h.actor_role ? ` (${h.actor_role})` : ""}</div>
                    {h.details && Object.keys(h.details).length > 0 && (
                      <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                        {Object.entries(h.details).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                          <span key={k} className="mr-3">{k}={String(v)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>
          </>
        )}
      </div>
      {viewingThread && (
        <AdminThreadViewerModal
          threadId={viewingThread}
          onClose={() => setViewingThread(null)}
        />
      )}
    </div>
  );
}

function ParticipantLine({ label, u }) {
  if (!u) return null;
  return (
    <div className="flex items-baseline gap-2 min-w-0" data-testid={`analytics-participant-${label.toLowerCase()}`}>
      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium truncate">{u.display_name || u.email || u.id || "—"}</span>
      {u.email && u.display_name && (
        <span className="text-[10px] text-muted-foreground truncate">({u.email})</span>
      )}
      {u.is_demo && <span className="text-[9px] px-1 rounded bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))] shrink-0">demo</span>}
      {u.is_private_test && <span className="text-[9px] px-1 rounded bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold-2))] shrink-0">pta</span>}
    </div>
  );
}

/**
 * Phase B.2: project-level confidentiality tally shown in the admin
 * project-detail modal. Mirrors the per-reader chips rendered on each
 * accepted application below.
 */
function ConfidentialitySummary({ summary }) {
  if (!summary || !summary.total) return null;
  const { accepted = 0, pending = 0, declined = 0, total = 0 } = summary;
  const lockedCount = total - accepted;
  return (
    <div
      className="mt-4 rounded-lg border border-[hsl(var(--sb-forest))]/30 bg-[hsl(var(--sb-moss))]/10 p-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-2"
      data-testid="analytics-confidentiality-summary"
    >
      <div className="flex items-center gap-1.5 font-medium text-[hsl(var(--sb-forest-2))]">
        <ShieldCheck size={13} />
        <span>Confidentiality</span>
      </div>
      <div data-testid="analytics-conf-summary-counts">
        <strong>{accepted}</strong> of <strong>{total}</strong> accepted
        {pending > 0 && <> · <strong>{pending}</strong> pending</>}
        {declined > 0 && <> · <strong>{declined}</strong> declined</>}
      </div>
      <div
        className="text-muted-foreground"
        data-testid="analytics-conf-summary-manuscript-access"
      >
        Manuscript access:{" "}
        <strong>{accepted}</strong> unlocked
        {lockedCount > 0 && <> · <strong>{lockedCount}</strong> locked</>}
      </div>
    </div>
  );
}

/**
 * Phase B.2: per-application confidentiality + manuscript-access chips.
 * Only rendered on applications where the backend attached a
 * `confidentiality` block (status is `accepted` or
 * `confidentiality_declined`).
 */
function ApplicationConfidentialityChips({ conf, appId }) {
  const state = conf.state || "pending";
  const confBase =
    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]";
  let confLabel = "Pending";
  let confTone =
    "border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay-2))]";
  let timing = conf.pending_since
    ? ` · pending since ${new Date(conf.pending_since).toLocaleDateString()}`
    : "";
  if (state === "accepted") {
    confLabel = `Accepted · v${conf.version || "?"}`;
    confTone =
      "border-[hsl(var(--sb-forest))]/50 bg-[hsl(var(--sb-moss))]/40 text-[hsl(var(--sb-forest-2))]";
    timing = conf.accepted_at
      ? ` · ${new Date(conf.accepted_at).toLocaleDateString()}`
      : "";
  } else if (state === "declined") {
    confLabel = "Declined";
    confTone = "border-destructive/40 bg-destructive/10 text-destructive";
    timing = conf.declined_at
      ? ` · ${new Date(conf.declined_at).toLocaleDateString()}`
      : "";
  }
  const unlocked = !!conf.manuscript_access_unlocked;
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-1.5"
      data-testid={`analytics-application-conf-${appId}`}
    >
      <span
        className={`${confBase} ${confTone}`}
        data-testid={`analytics-application-conf-state-${appId}`}
        data-conf-state={state}
      >
        {confLabel}
      </span>
      <span className="text-[10px] text-muted-foreground">{timing}</span>
      <span
        className={`${confBase} ${
          unlocked
            ? "border-[hsl(var(--sb-forest))]/50 bg-[hsl(var(--sb-forest))]/8 text-[hsl(var(--sb-forest-2))]"
            : "border-border bg-muted/60 text-muted-foreground"
        }`}
        data-testid={`analytics-application-access-${appId}`}
      >
        {unlocked ? "Manuscript unlocked" : "Manuscript locked"}
      </span>
    </div>
  );
}


/**
 * Read-only moderation view of any thread. Backed by
 * `GET /api/admin/analytics/threads/{thread_id}`, which is admin-only.
 * Does not mark messages as read and does not allow posting — purely for
 * investigating reports of inappropriate behaviour on the platform.
 */
function AdminThreadViewerModal({ threadId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/analytics/threads/${threadId}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [threadId]);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="admin-thread-viewer-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-background shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.16em] text-[hsl(var(--sb-gold))]">Moderation — thread</div>
            <h4 className="mt-1 font-serif text-xl truncate">
              {data?.project?.title || (loading ? "Loading…" : "Direct message")}
            </h4>
            {data && (
              <>
                <div className="mt-1 text-[11px] text-muted-foreground font-mono truncate" title={data.thread?.id}>{data.thread?.id}</div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Participants:{" "}
                  {(data.thread?.participants || []).map((p, i) => (
                    <span key={p?.id || i}>
                      {i > 0 && " · "}
                      <span className="font-medium"><ProfileNameLink user={p} testId={`admin-thread-heading-profile-${p?.id}`}>{p?.display_name || p?.email || p?.id || "—"}</ProfileNameLink></span>
                      {p?.email && p?.display_name && <span className="text-muted-foreground"> ({p.email})</span>}
                    </span>
                  ))}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  Status: {data.thread?.status} · Started {data.thread?.created_at ? new Date(data.thread.created_at).toLocaleString() : "—"}
                </div>
              </>
            )}
          </div>
          <button onClick={onClose} className="text-sm rounded-full border border-border px-3 py-1 hover:bg-muted shrink-0" data-testid="admin-thread-viewer-close">Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading && <div className="text-sm text-muted-foreground text-center py-8">Loading conversation…</div>}
          {!loading && (data?.messages || []).length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">No messages in this thread yet.</div>
          )}
          {(data?.messages || []).map((m) => (
            <div key={m.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`admin-thread-message-${m.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap text-[11px] mb-1">
                <div className="font-medium min-w-0 truncate">
                  <ProfileNameLink user={m.sender} testId={`admin-thread-message-profile-${m.id}`}>{m.sender?.display_name || m.sender?.email || m.sender?.id || "—"}</ProfileNameLink>
                  {m.sender?.email && m.sender?.display_name && <span className="text-muted-foreground ml-1">({m.sender.email})</span>}
                  {m.sender?.is_demo && <span className="ml-1 text-[9px] px-1 rounded bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))]">demo</span>}
                  {m.sender?.is_private_test && <span className="ml-1 text-[9px] px-1 rounded bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold-2))]">pta</span>}
                </div>
                <div className="text-muted-foreground whitespace-nowrap">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
              </div>
              <div className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-border p-3 text-[11px] text-muted-foreground bg-muted/40">
          Read-only moderation view. Messages remain unread for their recipients.
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

// ─── Insights panel ─────────────────────────────────────────────────────
function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function InsightMetric({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="font-serif text-base leading-tight truncate">{value ?? "—"}</div>
    </div>
  );
}

function PriorityRow({ row }) {
  return (
    <div className="rounded-xl border border-border bg-[hsl(var(--sb-surface))] p-4 hover:border-[hsl(var(--sb-gold))]/60 transition-colors" data-testid={`insights-row-${row.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-serif text-lg leading-tight truncate">{row.title}</h4>
            <StatusBadge status={row.status} />
            {row.is_demo && (
              <span className="inline-flex rounded-full border border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay))] text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5">Demo</span>
            )}
          </div>
          <div className="mt-1 text-xs text-muted-foreground truncate">
            by {row.author_name || row.author_id || "—"}
          </div>
        </div>
        <a
          href={`/jobs/${row.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] hover:bg-muted whitespace-nowrap"
          data-testid={`insights-open-${row.id}`}
        >
          Open <ExternalLink size={11} />
        </a>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <InsightMetric label="Last activity" value={fmtRelative(row.last_activity)} />
        <InsightMetric label="Applications" value={row.applications_count} />
        <InsightMetric label="Accepted readers" value={row.accepted_readers_count} />
        <InsightMetric label="Messages" value={row.messages_count} />
        <InsightMetric label="Coffee chats" value={row.coffee_chats_count} />
        <InsightMetric
          label={row.pending_apps > 0 ? "Pending apps" : "Created"}
          value={row.pending_apps > 0 ? row.pending_apps : fmtRelative(row.created_at)}
        />
      </div>
    </div>
  );
}

function InsightsPanel({ data, loading, includeDemo, onToggleDemo }) {
  if (loading) return <div className="mt-10 text-center text-muted-foreground">Loading insights…</div>;
  if (!data) return null;

  const list = data.priority_list || [];
  const grouped = PRIORITY_BUCKETS.map((b) => ({
    ...b,
    rows: list.filter((r) => r.priority_bucket === b.id),
  }));
  const h = data.headline || {};
  const t = data.thresholds || {};

  return (
    <div className="mt-6 space-y-6" data-testid="analytics-insights-panel">
      {/* Community counts callout — explains the 82 vs 92 style discrepancy */}
      <Card className="border-[hsl(var(--sb-gold))]/40">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--sb-gold-2))]">
              <Sparkles size={14} /> What needs your attention
            </div>
            <div className="mt-2 font-serif text-2xl leading-tight">
              {h.priority_actions ?? 0} project{h.priority_actions === 1 ? "" : "s"} on your desk right now
            </div>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              Priorities are ordered so the most time-sensitive items appear first:
              new applications, then anything waiting on your response, then quiet
              accepted projects, then stalled listings, then recent wins.
            </p>
          </div>
          <div className="flex flex-col sm:items-end gap-2">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={includeDemo}
                onChange={(e) => onToggleDemo(e.target.checked)}
                className="accent-[hsl(var(--sb-gold))]"
                data-testid="insights-include-demo-toggle"
              />
              Include demo / seed projects
            </label>
            <div className="text-[10px] text-muted-foreground">
              Thresholds: waiting {t.pending_app_days}d · idle {t.idle_days}d · stalled {t.stalled_days}d
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Real community</div>
            <div className="mt-1 font-serif text-2xl text-[hsl(var(--sb-forest-2))]">{h.real_community ?? 0}</div>
            <div className="text-[10px] text-muted-foreground">Matches homepage counter</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Demo / seed</div>
            <div className="mt-1 font-serif text-2xl">{h.demo_users ?? 0}</div>
            <div className="text-[10px] text-muted-foreground">Hidden from public counts</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Admin accounts</div>
            <div className="mt-1 font-serif text-2xl">{h.admin_users ?? 0}</div>
            <div className="text-[10px] text-muted-foreground">Also hidden from public counts</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Total users (raw)</div>
            <div className="mt-1 font-serif text-2xl">{h.total_users ?? 0}</div>
            <div className="text-[10px] text-muted-foreground">Real + demo + admin</div>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          The homepage community counter only shows the <strong>real community</strong> number
          so demo seed accounts and admin operators don&apos;t inflate the total. The Users tab
          shows the raw total for platform health tracking — this is why the two counts can differ.
        </p>
      </Card>

      {/* Priority buckets */}
      {list.length === 0 ? (
        <Card>
          <div className="py-10 text-center">
            <CheckCircle2 size={32} className="mx-auto text-[hsl(var(--sb-forest-2))]" />
            <div className="mt-3 font-serif text-xl">Nothing needs your attention right now.</div>
            <p className="mt-2 text-sm text-muted-foreground">
              All real projects are moving. Enjoy the quiet — or dive into the other tabs.
            </p>
          </div>
        </Card>
      ) : (
        grouped
          .filter((b) => b.rows.length > 0)
          .map((b) => {
            const Icon = b.icon;
            return (
              <section key={b.id} data-testid={`insights-bucket-${b.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] ${b.tone}`}>
                      <Icon size={12} /> {b.label}
                      <span className="ml-1 rounded-full bg-black/20 px-1.5 text-[10px]">{b.rows.length}</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground max-w-2xl">{b.description}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {b.rows.map((r) => <PriorityRow key={r.id} row={r} />)}
                </div>
              </section>
            );
          })
      )}
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────
export default function AdminAnalytics() {
  const { user } = useAuth();
  // Tab lives in the URL so Back from a profile returns to the same tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "insights";
  const setTab = useCallback((id) => setSearchParams((sp) => { const n = new URLSearchParams(sp); n.set("tab", id); return n; }, { replace: true }), [setSearchParams]);
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [signups, setSignups] = useState(null);
  const [verification, setVerification] = useState(null);
  const [projects, setProjects] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [terms, setTerms] = useState(null);
  const [settings, setSettings] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsIncludeDemo, setInsightsIncludeDemo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsFilter, setTermsFilter] = useState("all");
  const [termsQuery, setTermsQuery] = useState("");
  const [earlyAccess, setEarlyAccess] = useState(null);
  const [eaFilter, setEaFilter] = useState("all");
  const [eaQuery, setEaQuery] = useState("");
  const [previewAgreement, setPreviewAgreement] = useState(false);
  const [previewComms, setPreviewComms] = useState(false);

  const isAdmin = (user?.roles || []).includes("admin");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, sg, vf, pr, en, tr, tm, st, ins, ea] = await Promise.all([
        api.get("/admin/analytics/overview").then((r) => r.data),
        api.get(`/admin/analytics/signups?days=${days}`).then((r) => r.data),
        api.get("/admin/analytics/verification").then((r) => r.data),
        api.get(`/admin/analytics/projects?days=${days}`).then((r) => r.data),
        api.get(`/admin/analytics/engagement?days=${days}`).then((r) => r.data),
        api.get(`/admin/analytics/traffic?days=${days}`).then((r) => r.data),
        api.get(`/admin/analytics/terms?status=${termsFilter}`).then((r) => r.data),
        api.get("/admin/analytics/settings").then((r) => r.data),
        api.get(`/admin/analytics/insights?include_demo=${insightsIncludeDemo}`).then((r) => r.data),
        api.get(`/admin/analytics/early-access?status=${eaFilter}`).then((r) => r.data),
      ]);
      setOverview(ov); setSignups(sg); setVerification(vf); setProjects(pr);
      setEngagement(en); setTraffic(tr); setTerms(tm); setSettings(st);
      setInsights(ins);
      setEarlyAccess(ea);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [days, termsFilter, insightsIncludeDemo, eaFilter]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const toggleLaunch = async (next) => {
    try {
      const { data } = await api.patch("/admin/analytics/settings", { launch_mode: next });
      setSettings(data);
      toast.success(next ? "Launch Mode enabled — gate is now enforced." : "Launch Mode disabled.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filteredTermsRows = useMemo(() => {
    const q = termsQuery.trim().toLowerCase();
    const rows = terms?.rows || [];
    if (!q) return rows;
    return rows.filter((r) =>
      (r.email || "").toLowerCase().includes(q) ||
      (r.display_name || "").toLowerCase().includes(q)
    );
  }, [terms, termsQuery]);

  const filteredEaRows = useMemo(() => {
    const q = eaQuery.trim().toLowerCase();
    const rows = earlyAccess?.rows || [];
    if (!q) return rows;
    return rows.filter((r) =>
      (r.email || "").toLowerCase().includes(q) ||
      (r.display_name || "").toLowerCase().includes(q)
    );
  }, [earlyAccess, eaQuery]);

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const verifPie = verification ? [
    { name: "Verified", value: verification.verified, fill: C.forest },
    { name: "Unverified", value: verification.unverified, fill: C.clay },
  ] : [];

  const termsPie = overview ? [
    { name: "Accepted", value: overview.users.terms_accepted, fill: C.forest },
    { name: "Pending", value: overview.users.terms_pending, fill: C.amber },
  ] : [];

  const chartAxis = { stroke: C.inkDim, fontSize: 11 };

  return (
    <SiteShell>
      <div className="container-page py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="chip"><BarChart3 size={14} /> Admin only</div>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">Analytics</h1>
            <p className="mt-2 text-sm text-muted-foreground">Live activity on Last Bookmark. Real data from MongoDB.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
              data-testid="analytics-range-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted"
              data-testid="analytics-refresh"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-6">
          <LaunchModeCard
            settings={settings}
            onChange={toggleLaunch}
            previewHref="/dashboard?preview_terms_gate=1"
          />
        </div>

        {/* tabs */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-border">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`analytics-tab-${id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg -mb-px border-b-2 transition-colors ${
                tab === id
                  ? "border-[hsl(var(--sb-gold))] text-[hsl(var(--sb-forest-2))]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* insights — default landing tab */}
        {tab === "insights" && (
          <InsightsPanel
            data={insights}
            loading={loading && !insights}
            includeDemo={insightsIncludeDemo}
            onToggleDemo={setInsightsIncludeDemo}
          />
        )}

        {/* overview */}
        {tab === "overview" && overview && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Total users" value={overview.users.total} />
            <Stat label="Authors" value={overview.users.authors} />
            <Stat label="Beta readers" value={overview.users.readers} />
            <Stat label="Verified email" value={`${overview.users.verified} / ${overview.users.total}`} />
            <Stat label="Terms accepted" value={`${overview.users.terms_accepted} / ${overview.users.total}`} sub={`v${overview.current_terms_version}`} />
            <Stat label="Active (24h)" value={overview.activity.active_24h} />
            <Stat label="Active (7d)" value={overview.activity.active_7d} />
            <Stat label="Active (30d)" value={overview.activity.active_30d} />
            <Stat label="Projects total" value={overview.content.jobs_total} sub={`${overview.content.jobs_open} open`} />
            <Stat label="Applications" value={overview.content.applications_total} />
            <Stat label="Messages" value={overview.content.messages_total} />
            <Stat label="Coffee chats" value={overview.content.coffee_chats_total} />
          </div>
        )}

        {/* users */}
        {tab === "users" && signups && verification && (
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <ChartFrame title={`Signups over ${days} days`} right={<DownloadBtn metric="signups" days={days} />}>
              <ResponsiveContainer>
                <AreaChart data={signups.series}>
                  <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                  <XAxis dataKey="date" {...chartAxis} />
                  <YAxis {...chartAxis} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: C.moss, border: "1px solid " + C.grid, color: C.ink }} />
                  <Legend wrapperStyle={{ color: C.inkDim, fontSize: 11 }} />
                  <Area type="monotone" dataKey="authors" stackId="1" stroke={C.forest} fill={C.forest} fillOpacity={0.6} />
                  <Area type="monotone" dataKey="readers" stackId="1" stroke={C.amber} fill={C.amber} fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartFrame>

            <ChartFrame title="Verified vs unverified" right={<DownloadBtn metric="verification" days={days} />}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={verifPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                    {verifPie.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: C.moss, border: "1px solid " + C.grid, color: C.ink }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartFrame>

            <ChartFrame title="Verification trend (30d)">
              <ResponsiveContainer>
                <BarChart data={verification.trend}>
                  <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                  <XAxis dataKey="date" {...chartAxis} />
                  <YAxis {...chartAxis} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: C.moss, border: "1px solid " + C.grid, color: C.ink }} />
                  <Bar dataKey="verified" fill={C.forest} />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
          </div>
        )}

        {/* projects */}
        {tab === "projects" && projects && (
          <div className="mt-6 space-y-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <ChartFrame title={`New projects (${days}d)`} right={<DownloadBtn metric="projects" days={days} />}>
                <ResponsiveContainer>
                  <LineChart data={projects.series}>
                    <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                    <XAxis dataKey="date" {...chartAxis} />
                    <YAxis {...chartAxis} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: C.moss, border: "1px solid " + C.grid, color: C.ink }} />
                    <Line type="monotone" dataKey="new" stroke={C.amber} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartFrame>
              <Card>
                <h3 className="font-serif text-lg mb-3">Status breakdown</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(projects.status_breakdown || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between rounded-lg border border-border p-3">
                      <span className="text-sm capitalize">{k}</span>
                      <span className="font-serif text-xl">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-lg border border-border p-3 text-sm">
                  <div className="flex justify-between"><span>Total applications</span><span className="font-serif">{projects.applications_total}</span></div>
                  <div className="mt-1 flex justify-between text-muted-foreground"><span>Avg per project</span><span>{projects.applications_avg_per_project}</span></div>
                </div>
              </Card>
            </div>
            <ProjectManagementTable />
          </div>
        )}

        {/* engagement */}
        {tab === "engagement" && engagement && (
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <ChartFrame title={`Messages & Coffee chats (${days}d)`} right={<DownloadBtn metric="engagement" days={days} />}>
              <ResponsiveContainer>
                <LineChart data={engagement.series}>
                  <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                  <XAxis dataKey="date" {...chartAxis} />
                  <YAxis {...chartAxis} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: C.moss, border: "1px solid " + C.grid, color: C.ink }} />
                  <Legend wrapperStyle={{ color: C.inkDim, fontSize: 11 }} />
                  <Line type="monotone" dataKey="messages" stroke={C.forest} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="coffee_chats" stroke={C.amber} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartFrame>
            <Card>
              <h3 className="font-serif text-lg mb-3">Coffee chat status</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(engagement.coffee_status_breakdown || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="text-sm capitalize">{k}</span>
                    <span className="font-serif text-xl">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-lg border border-border p-3 text-sm flex justify-between">
                <span>Favorites total</span><span className="font-serif">{engagement.favorites_total}</span>
              </div>
            </Card>
          </div>
        )}

        {/* conversations — global admin moderation feed */}
        {tab === "conversations" && <AdminConversationsPanel />}

        {/* traffic */}
        {tab === "traffic" && traffic && (
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <ChartFrame title={`Active users per day (${days}d)`} right={<DownloadBtn metric="traffic" days={days} />}>
              <ResponsiveContainer>
                <BarChart data={traffic.series}>
                  <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                  <XAxis dataKey="date" {...chartAxis} />
                  <YAxis {...chartAxis} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: C.moss, border: "1px solid " + C.grid, color: C.ink }} />
                  <Bar dataKey="active_users" fill={C.forest} />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
            <div className="grid gap-4 sm:grid-cols-3">
              <Stat label="Last 24h" value={traffic.active_24h} />
              <Stat label="Last 7d" value={traffic.active_7d} />
              <Stat label="Last 30d" value={traffic.active_30d} />
            </div>
          </div>
        )}

        {/* terms */}
        {tab === "terms" && terms && (
          <div className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Stat label="Accepted" value={`${terms.accepted} / ${terms.total}`} />
              <Stat label="Pending" value={terms.pending} />
              <Stat label="Current version" value={`v${terms.current_version}`} />
            </div>
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-serif text-lg">User acceptance log</h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="search"
                      placeholder="Search email or name"
                      value={termsQuery}
                      onChange={(e) => setTermsQuery(e.target.value)}
                      className="rounded-full border border-border bg-background pl-8 pr-3 py-1.5 text-sm w-56"
                      data-testid="analytics-terms-search"
                    />
                  </div>
                  <select
                    value={termsFilter}
                    onChange={(e) => setTermsFilter(e.target.value)}
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
                    data-testid="analytics-terms-filter"
                  >
                    <option value="all">All users</option>
                    <option value="accepted">Accepted only</option>
                    <option value="pending">Pending only</option>
                  </select>
                  <DownloadBtn metric="terms" label="Export CSV" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4">User</th>
                      <th className="py-2 pr-4">Roles</th>
                      <th className="py-2 pr-4">Accepted</th>
                      <th className="py-2 pr-4">Version</th>
                      <th className="py-2 pr-4">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTermsRows.map((r) => (
                      <tr key={r.id} className="border-b border-border/60">
                        <td className="py-2 pr-4">
                          <div className="font-medium">{r.display_name || r.email}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </td>
                        <td className="py-2 pr-4 text-xs text-muted-foreground">
                          {(r.roles || []).join(", ")}
                        </td>
                        <td className="py-2 pr-4">
                          {r.accepted ? (
                            <span className="inline-flex items-center gap-1 text-[hsl(var(--sb-forest-2))]"><Check size={14} /> Accepted</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[hsl(var(--sb-clay-2))]"><X size={14} /> Pending</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-xs">{r.version_accepted || "—"}</td>
                        <td className="py-2 pr-4 text-xs text-muted-foreground">{r.accepted_at ? new Date(r.accepted_at).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                    {filteredTermsRows.length === 0 && (
                      <tr><td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">No users match.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* early access */}
        {tab === "early_access" && earlyAccess && (
          <div className="mt-6 space-y-4" data-testid="analytics-early-access-pane">
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-serif text-lg">Preview the pop-ups</h3>
                  <p className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed">
                    Open either Early Access pop-up here at any time to review the exact wording and design members see. Previewing does not change your acceptance status or affect any user data.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setPreviewAgreement(true)}
                    className="btn-primary inline-flex items-center gap-1.5 text-sm"
                    data-testid="analytics-preview-agreement"
                  >
                    <ScrollText size={14} /> Preview: Community Agreement
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewComms(true)}
                    className="btn-ghost border inline-flex items-center gap-1.5 text-sm"
                    data-testid="analytics-preview-comms"
                  >
                    <ShieldCheck size={14} /> Preview: Keep the conversation on Last Bookmark
                  </button>
                </div>
              </div>
            </Card>

            <div className="grid gap-4 sm:grid-cols-3">
              <Stat
                label="Agreement accepted"
                value={`${earlyAccess.agreement_accepted} / ${earlyAccess.total}`}
                sub={`v${earlyAccess.current_version}`}
              />
              <Stat
                label="Comms notice acknowledged"
                value={`${earlyAccess.comms_notice_acked} / ${earlyAccess.total}`}
              />
              <Stat
                label="Current agreement version"
                value={`v${earlyAccess.current_version}`}
              />
            </div>
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-serif text-lg">Early Access acceptance log</h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="search"
                      placeholder="Search email or name"
                      value={eaQuery}
                      onChange={(e) => setEaQuery(e.target.value)}
                      className="rounded-full border border-border bg-background pl-8 pr-3 py-1.5 text-sm w-56"
                      data-testid="analytics-early-access-search"
                    />
                  </div>
                  <select
                    value={eaFilter}
                    onChange={(e) => setEaFilter(e.target.value)}
                    className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
                    data-testid="analytics-early-access-filter"
                  >
                    <option value="all">All Early Access</option>
                    <option value="accepted">Accepted only</option>
                    <option value="pending">Pending only</option>
                  </select>
                  <DownloadBtn metric="early_access" label="Export CSV" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4">User</th>
                      <th className="py-2 pr-4">Roles</th>
                      <th className="py-2 pr-4">Agreement</th>
                      <th className="py-2 pr-4">Version</th>
                      <th className="py-2 pr-4">Accepted</th>
                      <th className="py-2 pr-4">Comms notice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEaRows.map((r) => (
                      <tr key={r.id} className="border-b border-border/60" data-testid={`analytics-early-access-row-${r.id}`}>
                        <td className="py-2 pr-4">
                          <div className="font-medium">{r.display_name || r.email}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </td>
                        <td className="py-2 pr-4 text-xs text-muted-foreground">
                          {(r.roles || []).join(", ")}
                        </td>
                        <td className="py-2 pr-4">
                          {r.agreement_accepted ? (
                            <span className="inline-flex items-center gap-1 text-[hsl(var(--sb-forest-2))]"><Check size={14} /> Accepted</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[hsl(var(--sb-clay-2))]"><X size={14} /> Pending</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-xs">{r.agreement_version_accepted || "—"}</td>
                        <td className="py-2 pr-4 text-xs text-muted-foreground">{r.agreement_accepted_at ? new Date(r.agreement_accepted_at).toLocaleString() : "—"}</td>
                        <td className="py-2 pr-4 text-xs">
                          {r.comms_notice_acked ? (
                            <span className="inline-flex items-center gap-1 text-[hsl(var(--sb-forest-2))]" title={r.comms_notice_acked_at ? new Date(r.comms_notice_acked_at).toLocaleString() : ""}>
                              <Check size={14} /> {r.comms_notice_acked_at ? new Date(r.comms_notice_acked_at).toLocaleDateString() : "Acked"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-muted-foreground"><X size={14} /> Pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredEaRows.length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No Early Access members match.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>
      {previewAgreement && (
        <EarlyAccessCommunityAgreementModal
          previewMode
          onClose={() => setPreviewAgreement(false)}
        />
      )}
      {previewComms && (
        <EarlyAccessCommsNoticeModal
          previewMode
          onClose={() => setPreviewComms(false)}
        />
      )}
    </SiteShell>
  );
}
